import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import s from './MultiSelect.module.scss';
import ButtonPrimary from '../ButtonPrimary/ButtonPrimary';
import Image from 'next/image';

const MultiSelect = ({
  options = [],
  value = [],
  onChange,
  placeholder = '',
  activeMainCategories = [],
  onSelectMainCategory,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  // normalize so we can handle both {value,label} and {slug,title}
  const normalizedOptions = options.map((o) => ({
    value: o.value || o.slug,
    label: o.label || o.title,
    icon: o.icon,
    subCategories: (o.subCategories || []).map((s) => ({
      value: s.value || s.slug,
      label: s.label || s.title,
    })),
  }));

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  useEffect(() => {
    if (!activeMainCategories.length) return;

    const allowedValues = normalizedOptions
      .filter((o) => activeMainCategories.includes(o.value))
      .flatMap((o) => o.subCategories.map((s) => s.value));

    const filtered = value.filter((v) => allowedValues.includes(v));

    if (filtered.length !== value.length) {
      onChange?.(filtered);
    }
  }, [activeMainCategories, normalizedOptions, value, onChange]);

  const toggleValue = (val, mainCatValue) => {
    let newValues;
    if (value.includes(val)) {
      newValues = value.filter((v) => v !== val);
    } else {
      newValues = [...value, val];

      // aktivera huvudkategorin också
      onSelectMainCategory?.(mainCatValue);
    }
    onChange?.(newValues);
  };

  return (
    <div className={s.Root} ref={wrapperRef}>
      <div>
        <ButtonPrimary
          isToggle
          modifiers={['InlineFlex']}
          onClick={() => setIsOpen(!isOpen)}>
          <span>{placeholder}</span>
          {value.length > 0 && (
            <div className={s.ActiveFiltersCount}>
              <span>{value.length}</span>
            </div>
          )}
        </ButtonPrimary>

        {isOpen && (
          <>
            <div className={s.Overlay} onClick={() => setIsOpen(false)} />
            <ul className={classNames('black-glass', s.Options)}>
              {normalizedOptions.map((mainCat, i) => {
                return (
                  <React.Fragment key={i}>
                    <li className={classNames(s.MainCategory)}>
                      {mainCat.label}
                    </li>
                    {mainCat.subCategories.map((sub) => {
                      const isSelected = value.includes(sub.value);

                      return (
                        <li
                          key={sub.value}
                          role="option"
                          aria-selected={isSelected}
                          tabIndex={0}
                          className={classNames(s.Option, {
                            [s.Selected]: isSelected,
                          })}
                          onClick={() => toggleValue(sub.value, mainCat.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleValue(sub.value, mainCat.value);
                            }
                          }}>
                          <span>{sub.label}</span>
                          {isSelected && (
                            <Image
                              width={20}
                              height={20}
                              src="/img/icon-check.svg"
                              alt=""
                              className={s.Check}
                            />
                          )}
                        </li>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default MultiSelect;
